import RootState from '../state'
import { ThunkDispatch } from 'redux-thunk'
import AppTypes from './app.types'
import { getAppConfig } from '../../services/api'
import { mergeDeep, postMessageToParentPromise, setDisableWarningWhenLeaving } from 'utils'
import { getUrlParameters, addStyle } from 'utils'
import AppState, { ChatMode, IAppConfigResponse } from './app.state'
import { MessagesAction } from 'store/messages/messages.actions'
import * as Logger from 'services/logger'
import { translations, Translations, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from 'translations'

type AppAction =
    | { type: AppTypes.INIT_APP_LOADING }
    | { type: AppTypes.INIT_DISCONNECT_FLOW; payload: IAppConfigResponse }
    | { type: AppTypes.SET_CUSTOMER_ENDED_CHAT; payload: boolean }
    | {
          type: AppTypes.INIT_APP
          payload: IAppConfigResponse & { deletePersistanceContact?: boolean }
      }
    | { type: AppTypes.END_CHAT; payload?: { preventClosingChatWindow?: boolean } }
    | { type: AppTypes.CUSTOMER_ENDED_CHAT }
    | { type: AppTypes.START_NEW_CHAT }
    | { type: AppTypes.INIT_APP_ERROR; payload: { errorMessage: string } }
    | { type: AppTypes.SET_MESSAGE_INPUT; payload: string }
    | { type: AppTypes.SET_AGENT_TYPING; payload: { isTyping: boolean; agentName?: string } }
    | { type: AppTypes.SET_BOT_TYPING; payload: boolean }
    | { type: AppTypes.SET_AGENT_CONNECTED; payload: boolean }
    | { type: AppTypes.SET_MODE; payload: ChatMode | undefined }
    | { type: AppTypes.SET_END_CHAT_CONFIRM; payload?: boolean }
    | { type: AppTypes.SET_LANGUAGE; payload: string }
    | { type: AppTypes.BATCH_UPDATE; payload: Partial<AppState> }
    | { type: AppTypes.CHAT_EVENT }
    | {
          type: AppTypes.SET_AUTO_TRANSLATE_CONFIRM
          payload: { confirm: boolean; language?: string[] }
      }
    | { type: AppTypes.SET_TRANSLATION_LANGUAGE; payload?: string }
    | { type: AppTypes.SET_TRANSLATION_STATUS; payload: boolean }
    | { type: AppTypes.SET_GLOBAL_PAGE_STATUS; payload: string | null }
    | { type: AppTypes.SET_APP_TEXT_TRANSLATIONS; payload: Translations }
    | { type: AppTypes.SET_PENDING_ATTACHMENT; payload: { id: string, name: string, type: string } }

export const startNewChat = () => {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction | MessagesAction>,
        getState: () => RootState,
    ) => {
        dispatch({
            type: AppTypes.START_NEW_CHAT,
        })
        dispatch({
            type: AppTypes.INIT_APP,
            payload: {
                ...getState().app,
                deletePersistanceContact: true,
            },
        })
    }
}

export const setAgentConnected = (connected = true): AppAction => ({
    type: AppTypes.SET_AGENT_CONNECTED,
    payload: connected,
})

export const setEndChatConfirm = (confirm = true): AppAction => ({
    type: AppTypes.SET_END_CHAT_CONFIRM,
    payload: confirm,
})

export const setCustomerEndedChat = (customerEnded = true): AppAction => ({
    type: AppTypes.SET_CUSTOMER_ENDED_CHAT,
    payload: customerEnded,
})

export const setAutoTranslateConfirm = (confirm = true, language?: string[]): AppAction => ({
    type: AppTypes.SET_AUTO_TRANSLATE_CONFIRM,
    payload: {
        confirm,
        language,
    },
})

export const setAutoTranslateLanguage = (language?: string): AppAction => ({
    type: AppTypes.SET_TRANSLATION_LANGUAGE,
    payload: language,
})

export const setTranslationStatus = (on: boolean): AppAction => ({
    type: AppTypes.SET_TRANSLATION_STATUS,
    payload: on,
})

export const endChat = () => {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction | MessagesAction>,
        getState: () => RootState,
    ) => {
        dispatch(setCustomerEndedChat())
        const state = getState().app
        const { connectionDetails } = state

        if (state.endChatConfirm && state.translation) dispatch(setTranslationStatus(false))

        dispatch(setEndChatConfirm(false))

        // If the config has a disconnect flow then run the disconnect flow if the customer ends the chat and they are currently connected to an agent. This checks customerDisconnectFlowStarted so the customer initiated disconnect flow should only run once
        if (
            connectionDetails?.customerDisconnectFlowId &&
            !state.customerDisconnectFlowStarted &&
            state.connectedToAgent
        ) {
            console.log('sa-log start disconnect chat flow')

            dispatch({
                type: AppTypes.INIT_DISCONNECT_FLOW,
                payload: state,
            })
        } else {
            if (state.endChatConfirm) {
                dispatch({
                    type: AppTypes.CUSTOMER_ENDED_CHAT,
                    payload: state,
                })
            }
            console.log('end chat normally')
            dispatch({
                type: AppTypes.END_CHAT,
            })
        }
    }
}

export const initAppLoading = (): AppAction => ({
    type: AppTypes.INIT_APP_LOADING,
})

export const initApp = () => {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction>,
        // getState: () => RootState
    ) => {
        performance.mark('init-app-start')

        connect.ChatSession.setGlobalConfig({
            // @ts-ignore
            features: {
                messageReceipts: {
                    shouldSendMessageReceipts: true,
                    thorttleTime: 5000,
                },
            },
        })

        dispatch(initAppLoading())
        let appLanguage: string | null = null

        try {
            const { company, facia, chatFlowId, language } = getUrlParameters()
            appLanguage = language

            Logger.init(company)

            if (appLanguage && SUPPORTED_LANGUAGES.includes(appLanguage)) {
                dispatch({
                    type: AppTypes.SET_LANGUAGE,
                    payload: appLanguage,
                })
            }

            performance.mark('get-config-start')

            const response =
                (await postMessageToParentPromise('get-app-config', 'receive-app-config')) ??
                (await getAppConfig(company, facia ?? company, chatFlowId)).data

            console.log('response', response)

            performance.mark('get-config-end')

            let themeStyles = ''
            Object.entries(response.theme).forEach(
                ([varName, varValue]) => (themeStyles += `--sa-${varName}: ${varValue};`),
            )

            if (themeStyles) addStyle(`.sa-chat-wrapper {${themeStyles}}`)

            if (response.customStyles) addStyle(response.customStyles)

            const fontSources = response.fontSources

            fontSources?.forEach((fs) => {
                const font = new FontFace(fs.name, `url(${fs.src})`)

                font.load()
                    .then((font) => document.fonts.add(font as any))
                    .catch((error) => {
                        Logger.warn(`Unable to load font`, { font: fs.name })
                        console.log(error)
                    })
            })

            setDisableWarningWhenLeaving(response?.disableOnCloseConfirmAlert)

            const appTextTranslations = response.appTextTranslationOverrides
                ? mergeDeep(translations, response.appTextTranslationOverrides)
                : translations

            dispatch({
                type: AppTypes.SET_APP_TEXT_TRANSLATIONS,
                payload: appTextTranslations,
            })

            dispatch({
                type: AppTypes.INIT_APP,
                payload: response,
            })
        } catch (error) {
            console.error(error)
            Logger.error('Failed to initialise app', error)

            const networkConnectionError =
                translations?.networkConnection?.[appLanguage ?? DEFAULT_LANGUAGE]?.networkError ??
                translations?.networkConnection?.[DEFAULT_LANGUAGE]?.networkError

            dispatch({
                type: AppTypes.INIT_APP_ERROR,
                payload: {
                    errorMessage: networkConnectionError!,
                },
            })
        }
    }
}

export const setMessageInput = (input: string): AppAction => ({
    type: AppTypes.SET_MESSAGE_INPUT,
    payload: input,
})

export const setAgentTyping = (isTyping: boolean = true, agentName?: string): AppAction => ({
    type: AppTypes.SET_AGENT_TYPING,
    payload: {
        isTyping,
        agentName,
    },
})

export const setBotTyping = (isTyping: boolean = true): AppAction => ({
    type: AppTypes.SET_BOT_TYPING,
    payload: isTyping,
})

export const setMode = (mode: ChatMode | undefined): AppAction => ({
    type: AppTypes.SET_MODE,
    payload: mode,
})

export const setGlobalPageStatus = (url: boolean) => ({
    type: AppTypes.SET_GLOBAL_PAGE_STATUS,
    payload: url,
})

export const setPendingAttachment = (fileData: { id: string, name: string, type: string }): AppAction => {
    return {
        type: AppTypes.SET_PENDING_ATTACHMENT,
        payload: fileData
    }
}

export default AppAction;
